import React from 'react';
import { Typography, Table } from 'antd';
import { CostDistributionPayload } from 'domains/orderDomain';
import { RevxMoney } from 'components/RevxMoney';

const { Text } = Typography;

/** A job is billed through cost distribution as soon as it carries any of these. */
export const hasCostDistribution = (data?: CostDistributionPayload) =>
  Boolean(
    data &&
      ((data.oracleSettings && data.oracleSettings.length > 0) ||
        (data.categories && data.categories.length > 0) ||
        data.manualOracleString?.trim())
  );

interface Props {
  data?: CostDistributionPayload;
  /** Suppliers and buyers do not see the money behind the distribution. */
  hidePrice?: boolean;
}

const CostDistributionDisplay: React.FC<Props> = ({ data, hidePrice }) => {
  if (!data || !data.oracleSettings || data.oracleSettings.length <= 0)
    return null;

  const columns = [
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: (text: string, record: any) => (
        <Text strong={record.level === 0}>{text || '-'}</Text>
      ),
    },
    {
      title: 'Cost Center / Site',
      dataIndex: 'site',
      key: 'site',
      width: 200,
      render: (text: string, record: any) => (
        <div style={{ paddingLeft: record.level > 0 ? 16 : 0 }}>
          {record.level > 0 && (
            <span style={{ color: '#bfbfbf', marginRight: 8 }}>└</span>
          )}
          <Text>{text || '-'}</Text>
        </div>
      ),
    },
    {
      title: 'Banner',
      dataIndex: 'banner',
      key: 'banner',
      width: 150,
      render: (text: string, record: any) => (
        <div style={{ paddingLeft: record.level > 1 ? 16 : 0 }}>
          {record.level > 1 && (
            <span style={{ color: '#bfbfbf', marginRight: 8 }}>└</span>
          )}
          <Text type={record.level > 1 ? 'secondary' : undefined}>
            {text || '-'}
          </Text>
        </div>
      ),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right' as const,
      width: 100,
      render: (v: any) => v ?? '-',
    },
    {
      title: 'Percent',
      dataIndex: 'percent',
      key: 'percent',
      align: 'right' as const,
      width: 100,
      render: (v: any) => (v !== undefined ? `${v}%` : '-'),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right' as const,
      width: 130,
      render: (v: any) => <RevxMoney value={v || 0} />,
    },
  ].filter((column) => !(hidePrice && column.key === 'amount'));

  const dataSource: any[] = [];

  // Process all categories
  data.categories?.forEach((cat, cIdx) => {
    if (cat.subSplits && cat.subSplits.length > 0) {
      cat.subSplits.forEach((sub, sIdx) => {
        // Extract site/cost center info from siteName
        // siteName format: "Cost Center - description" or "Site - description"
        const siteName =
          sub.description || sub.siteName || String(sub.configId || '');

        if (sub.banners && sub.banners.length > 0) {
          // If there are banners, show each banner as a row
          sub.banners.forEach((b, bIdx) => {
            dataSource.push({
              key: `cat-${cIdx}-sub-${sIdx}-b-${bIdx}`,
              category: cat.categoryName || cat.categoryId,
              site: siteName,
              banner: b.bannerName || String(b.bannerId || ''),
              quantity: b.quantity,
              percent: b.percent,
              amount: b.amount,
              level: 2, // Banner level
            });
          });
        } else {
          // No banners, show just site/cost center
          dataSource.push({
            key: `cat-${cIdx}-sub-${sIdx}`,
            category: cat.categoryName || cat.categoryId,
            site: siteName,
            banner: sub.banner || '-',
            quantity: sub.quantity,
            percent: sub.percent,
            amount: sub.amount,
            level: 1, // Site level
          });
        }
      });
    } else {
      // Category without subSplits
      dataSource.push({
        key: `cat-${cIdx}`,
        category: cat.categoryName || cat.categoryId,
        site: '-',
        banner: '-',
        quantity: cat.quantity,
        percent: cat.percent,
        amount: cat.amount,
        level: 0, // Category level
      });
    }
  });

  return (
    <div className="cost-distribution-display mt-20">
      <div className="title" style={{ marginBottom: '12px' }}>
        <i className="allow" />
        <Text strong style={{ fontSize: '15px', color: '#009088' }}>
          Cost Distribution
        </Text>
      </div>

      <div style={{ padding: '0 24px' }}>
        {/* 非手动输入时显示表格 */}
        {!data.isManualOracleString && (
          <Table
            dataSource={dataSource}
            columns={columns}
            pagination={false}
            size="small"
            bordered
            style={{ marginBottom: '16px' }}
          />
        )}

        {/* Oracle String 信息展示 */}
        {data.oracleSettings && data.oracleSettings.length > 0 && (
          <div
            style={{
              marginBottom: '16px',
              border: '1px solid #f0f0f0',
              borderRadius: '4px',
            }}
          >
            <div
              style={{
                padding: '12px 16px',
                background: '#fafafa',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <Text strong>
                Oracle Billing Strings
                {data.isManualOracleString && ' (Manually Set)'}
              </Text>
            </div>
            <div style={{ padding: '12px 16px' }}>
              {data.oracleSettings.map((setting, idx) => (
                <div
                  key={idx}
                  style={{
                    marginBottom:
                      idx < data.oracleSettings!.length - 1 ? '16px' : 0,
                    paddingBottom:
                      idx < data.oracleSettings!.length - 1 ? '16px' : 0,
                    borderBottom:
                      idx < data.oracleSettings!.length - 1
                        ? '1px solid #f0f0f0'
                        : 'none',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '8px',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: '4px' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          <Text strong>Category:</Text>{' '}
                          {setting.category || '-'}
                          {setting.site && (
                            <>
                              {' | '}
                              <Text strong>Site:</Text> {setting.site}
                              {(setting.description || setting.siteDesc) &&
                                ` (${setting.description || setting.siteDesc})`}
                            </>
                          )}
                          {setting.costCenter && (
                            <>
                              {' | '}
                              <Text strong>Cost Center:</Text>{' '}
                              {setting.costCenter}
                              {(setting.description ||
                                setting.costCenterDesc) &&
                                ` (${
                                  setting.description || setting.costCenterDesc
                                })`}
                            </>
                          )}
                          {setting.banner && (
                            <>
                              {' | '}
                              <Text strong>Banner:</Text> {setting.banner}
                            </>
                          )}
                          {setting.quantity !== undefined && (
                            <>
                              {' | '}
                              <Text strong>Qty:</Text> {setting.quantity}
                            </>
                          )}
                          {setting.percent !== undefined && (
                            <>
                              {' | '}
                              <Text strong>Percent:</Text> {setting.percent}%
                            </>
                          )}
                        </Text>
                      </div>
                    </div>
                    {!hidePrice && (
                      <Text
                        strong
                        style={{ marginLeft: '16px', whiteSpace: 'nowrap' }}
                      >
                        <RevxMoney value={setting.amount || 0} />
                      </Text>
                    )}
                  </div>
                  <div
                    style={{
                      padding: '8px 12px',
                      background: '#f5f5f5',
                      borderRadius: '4px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      wordBreak: 'break-all',
                    }}
                  >
                    <Text code>{setting.oracleString || '-'}</Text>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.comments && (
          <div style={{ marginBottom: '16px' }}>
            <Text strong>Comments: </Text>
            <Text>{data.comments}</Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default CostDistributionDisplay;
